import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authFetch } from "../services/authAPI";
import moment from "moment";
import "moment/locale/fr";
import Select from "../components/Select";
import Field from "../components/Field";
import Modal from "../components/Modal";
import { PRIORITIES_LABELS, PRIORITIES_CLASSES, STATUS_OPTIONS, STATUS_CLASSES, ROLE_LABELS, ROLE_CLASSES } from "../constants/tickets";

const STATUS_LABELS = STATUS_OPTIONS;

export default function TicketPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ticket, setTicket] = useState(null);
    const [priorities, setPriorities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [activityViolations, setActivityViolations] = useState([]);
    const [activityStatus, setActivityStatus] = useState("PENDING");
    const [activityDescription, setActivityDescription] = useState("");
    const [activitySearch, setActivitySearch] = useState("");

    async function fetchTicketPriorities() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/ticket_priorities`);
            const data = await response.json();
            setPriorities(data);
        } catch (err) {
            // silently fail
        }
    }

    async function fetchTicket() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/tickets/${id}`);
            if (!response.ok) {
                setError("Ticket introuvable.");
                return;
            }
            const data = await response.json();
            setTicket(data);
        } catch (err) {
            setError("Erreur lors du chargement du ticket.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchTicket();
        fetchTicketPriorities();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    async function handleCreateActivity() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/ticket_activities`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    status: activityStatus,
                    description: activityDescription,
                    ticket: `/api/tickets/${id}`,
                }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                setActivityViolations(errorData.violations ?? []);
                return;
            }
            setShowActivityModal(false);
            setActivityViolations([]);
            setActivityStatus("PENDING");
            setActivityDescription("");
            fetchTicket();
        } catch (err) {
            // silently fail
        }
    }

    if (loading) {
        return (
            <section>
                <div className="card mb-4">
                    <div className="card-body py-5 px-5 text-center">
                        <p className="text-muted">Chargement...</p>
                    </div>
                </div>
            </section>
        );
    }

    if (error || !ticket) {
        return (
            <section>
                <div className="card mb-4">
                    <div className="card-body py-5 px-5 text-center">
                        <p className="text-danger">{error ?? "Ticket introuvable."}</p>
                        <button className="btn btn-link" onClick={() => navigate("/tickets")}>
                            Retour aux tickets
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <>
        <section>
            <Modal
                title="Ajouter une activité"
                body={
                    <>
                        {activityViolations.length > 0 && (
                            <div className="alert alert-danger d-flex align-items-center" role="alert">
                                <i className="fas fa-times-circle me-3"></i>
                                <ul className="mb-0 list-unstyled">
                                    {activityViolations.map((v, i) => (
                                        <li key={i}>{v.message}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <Select
                            name="activityStatus"
                            label="Statut"
                            value={activityStatus}
                            onChange={(e) => setActivityStatus(e.target.value)}
                            options={Object.entries(STATUS_OPTIONS).map(([value, label]) => ({ value, label }))}
                        />
                        <Field
                            name="activityDescription"
                            label="Description"
                            value={activityDescription}
                            onChange={(e) => setActivityDescription(e.target.value)}
                            type="text"
                        />
                    </>
                }
                button={{ close: "Annuler", valid: "Créer" }}
                showModal={showActivityModal}
                setShowModal={setShowActivityModal}
                onSubmit={handleCreateActivity}
            />
            <div className="card mb-4">
                <div className="card-body py-5 px-5">
                    <div className="row g-4">
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Numéro de ticket</h6>
                            <p className="fw-bold mb-0">{ticket.ticketNumber}</p>
                        </div>
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase" style={{ fontSize: "0.75rem" }}>Créé le</h6>
                            <p className="mb-0">
                                {moment(ticket.createdAt).locale("fr").format("dddd Do MMMM YYYY [à] HH:mm")} par <span className={"badge badge-" + (ROLE_CLASSES[ticket.createdBy.role] ?? "secondary")}>
                                {ticket.createdBy.firstName} {ticket.createdBy.lastName}
                            </span>
                            </p>
                        </div>
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Véhicule</h6>
                            <p className="mb-0">
                                {ticket.vehicle?.licensePlateNumber}{" "}
                                <span className="text-muted">{ticket.vehicle?.name}</span>
                            </p>
                        </div>
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Kilométrage</h6>
                            <p className="mb-0">
                                {ticket.mileage != null ? `${ticket.mileage.toLocaleString("fr-FR")} km` : <span className="text-muted">—</span>}
                            </p>
                        </div>
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Priorité</h6>
                            <p className="mb-0">
                                <span className={"badge badge-" + (PRIORITIES_CLASSES[ticket.priority] ?? "secondary")}>
                                    {PRIORITIES_LABELS[ticket.priority] ?? ticket.priority}
                                </span>
                            </p>
                        </div>
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Statut</h6>
                            <p className="mb-0">{STATUS_LABELS[ticket.status] ?? <span className="text-muted">—</span>}</p>
                        </div>
                        {ticket.description && (
                            <div className="col-12">
                                <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Description</h6>
                                <p className="mb-0">{ticket.description}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
        <section>
            <div className="card mb-4">
                <div className="card-header py-3">
                    <div className="row py-3 align-items-center">
                        <div className="col-sm-6 text-center text-sm-start">
                            <h5 className="mb-4 mb-sm-0 mt-2">Activités <span className="text-muted">({ticket.totalActivities})</span></h5>
                        </div>
                        <div className="col-sm-6 text-center text-sm-end">
                            <button
                                type="button"
                                className="btn btn-primary me-2"
                                onClick={() => {
                                    setShowActivityModal(true);
                                    setActivityViolations([]);
                                }}
                            >
                                Ajouter
                                <i className="fas fa-plus ms-2"></i>
                            </button>
                        </div>
                    </div>
                </div>
                <div className="card-body py-5 px-5">
                    <div className="d-flex align-items-center mb-4">
                        <div className="flex-grow-1">
                            <Field
                                name="activitySearch"
                                label="Rechercher"
                                value={activitySearch}
                                onChange={(e) => setActivitySearch(e.target.value)}
                                type="text"
                                margin={false}
                            />
                        </div>
                    </div>
                    {(ticket.ticketActivities ?? [])
                        .filter((a) =>
                            a.description?.toLowerCase().includes(activitySearch.toLowerCase()) ||
                            STATUS_LABELS[a.status]?.toLowerCase().includes(activitySearch.toLowerCase()) ||
                            `${a.createdBy?.firstName} ${a.createdBy?.lastName}`.toLowerCase().includes(activitySearch.toLowerCase())
                        )
                        .map((activity) => (
                            <div key={activity.id} className="card mb-2">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <small className="text-muted">
                                            {activity.createdAt
                                                ? moment(activity.createdAt).locale("fr").format("dddd Do MMMM YYYY [à] HH:mm")
                                                : <span>—</span>}
                                            {" "}par{" "}
                                            {activity.createdBy
                                                ? <span className={"badge badge-" + (ROLE_CLASSES[activity.createdBy.role] ?? "secondary")}>
                                                    {activity.createdBy.firstName} {activity.createdBy.lastName}
                                                    </span>
                                                : <span>—</span>}
                                        </small>
                                        <span className={"badge badge-" + (STATUS_CLASSES[activity.status] ?? "secondary")}>
                                            {STATUS_LABELS[activity.status] ?? activity.status}
                                        </span>
                                    </div>
                                    <p className="mb-0">{activity.description ?? <span className="text-muted">—</span>}</p>
                                </div>
                            </div>
                        ))}
                </div>
            </div>
        </section>
    </>
    );
}
